import type { ReviewCreditCardsPage } from '@cc-shopping/contracts/credit-cards-page/reviews/schema/types';
import { env } from '@cc-shopping/platform-env';
import type { Person, Review, WithContext } from 'schema-dts';
import { ORGANIZATION_ID } from 'src/shared/seo/OrganizationLinkedData/utils';
import type { ReviewSchema } from './types';

export function getReviewSchema(review: ReviewCreditCardsPage): ReviewSchema {
  const { authors, link, marketplaceEntity, reviewedBy, seo, title } = review;
  const { consBullets, prosBullets } = marketplaceEntity;
  let authorsSchema: Person | Person[];
  const getAuthorSchema = (author: ReviewCreditCardsPage['authors'][number]) => ({
    '@type': 'Person',
    image: author.avatarUrls.large.url,
    name: author.name,
    jobTitle: author.userPage.jobTitle,
    url: author.link,
    description: author.description,
    ...(author.alumniOf && {
      alumniOf: {
        '@type': 'EducationalOrganization',
        name: author.alumniOf,
      },
    }),
    ...(author.affiliation && {
      affiliation: {
        '@type': 'Organization',
        name: author.affiliation,
      },
    }),
    ...(author.award && { award: author.award }),
    sameAs: author.socialMediaLinks.map(socialMediaLink => socialMediaLink.url),
  });
  if (authors.length > 1) {
    authorsSchema = authors.map(getAuthorSchema) as Person[];
  } else {
    authorsSchema = getAuthorSchema(authors[0]) as Person;
  }

  const schema: WithContext<Review> = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    author: authorsSchema,
    dateModified: review.modified,
    datePublished: review.date,
    description: seo.description,
    headline: title,
    inLanguage: 'en',
    itemReviewed: {
      '@type': 'Product',
      name: marketplaceEntity.displayName,
      brand: marketplaceEntity.institution.name,
      description: seo.description,
      image: marketplaceEntity.image?.large.url,
      offers: [
        {
          '@type': 'Offer',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: marketplaceEntity.details.annualFee?.value,
            priceCurrency: 'USD',
            unitText: 'annual fee',
          },
        },
      ],
    },
    publisher: {
      '@id': ORGANIZATION_ID,
      '@type': 'Organization',
      name: 'NerdWallet',
      legalName: 'NerdWallet, Inc.',
      url: `${env.NEXT_PUBLIC_NW_URL}author/nerdwallet`,
      logo: {
        '@type': 'ImageObject',
        url: `${env.NEXT_PUBLIC_NW_URL}assets/img/nw-logos/NW-default_og-image.jpg`,
      },
      foundingDate: '2009',
      sameAs: [
        'https://www.facebook.com/NerdWallet',
        'https://twitter.com/NerdWallet',
        'https://www.pinterest.com/nerdwallet',
        'http://nerdwallet.tumblr.com',
        'https://www.linkedin.com/company/nerdwallet',
        'https://plus.google.com/+nerdwallet',
        'https://www.glassdoor.com/Overview/Working-at-NerdWallet-EI_IE687336.11,21.htm',
        'https://www.instagram.com/nerdwallet',
      ],
      founder: [{ '@type': 'Person', name: 'Tim Chen' }],
      address: {
        '@type': 'PostalAddress',
        streetAddress: '875 Stevenson Street, 5th Floor',
        addressLocality: 'San Francisco',
        addressRegion: 'CA',
        postalCode: '94103',
        addressCountry: { '@type': 'Country', name: 'USA' },
      },
    },
    url: link,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': link,
      ...(reviewedBy && {
        reviewedBy: reviewedBy.map(reviewer => ({
          '@type': 'Person',
          image: reviewer.featuredMedia?.croppedImages[0].sourceUrl,
          name: reviewer.name,
          url: reviewer.link,
          sameAs: reviewer.socialMediaLinks.map(socialMediaLink => socialMediaLink.url),
        })),
      }),
      url: link,
    },
    positiveNotes: {
      '@type': 'ItemList',
      itemListElement: prosBullets.map((prosBullet, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: prosBullet,
        item: prosBullet,
      })),
    },
    negativeNotes: {
      '@type': 'ItemList',
      itemListElement: consBullets.map((consBullet, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: consBullet,
        item: consBullet,
      })),
    },
    ...(marketplaceEntity.starRating !== undefined && {
      reviewRating: {
        '@type': 'Rating',
        worstRating: 0,
        bestRating: 5,
        ratingValue: marketplaceEntity.starRating,
      },
    }),
  };
  return schema;
}
